/**
 * Hook for fetching a single opportunity's details
 * 
 * Isolates API calls from UI components per cursor rules.
 */

import { useState, useEffect, useCallback } from 'react';
import { authApi } from '../shared/utils/api';
import { OpportunityDetail } from '../shared/types/opportunity';
import { logger } from '../shared/utils/logger';

export function useOpportunity(portfolioId: number, opportunityId: number) {
  const [opportunity, setOpportunity] = useState<OpportunityDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOpportunity = useCallback(async () => {
    if (!portfolioId || !opportunityId) {
      setError('Portfolio ID or Opportunity ID is missing.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await authApi.getOpportunity(portfolioId, opportunityId);
      setOpportunity(response.opportunity);
    } catch (err) {
      logger.error(`Failed to fetch opportunity ${opportunityId}`, 'opportunity');
      setError('Failed to load opportunity details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [portfolioId, opportunityId]);

  useEffect(() => {
    fetchOpportunity();
  }, [fetchOpportunity]);

  return { opportunity, loading, error, fetchOpportunity };
}

